import React, { useState } from 'react';
import { AlertTriangle, X, Bug, Check } from 'lucide-react';
import { useGlobalError } from '../../context/GlobalErrorContext';
import { useTranslation } from 'react-i18next';
import { addDoc, collection } from 'firebase/firestore';
import { db, auth } from '../../config/firebase';

const ErrorModal = () => {
    const { error, clearError } = useGlobalError();
    const { t } = useTranslation();
    const [reporting, setReporting] = useState(false);
    const [reported, setReported] = useState(false);

    if (!error) return null;

    const handleReport = async () => {
        if (reported || reporting) return;
        setReporting(true);
        try {
            await addDoc(collection(db, 'error_reports'), {
                userId: auth.currentUser?.uid || 'anonymous',
                error: {
                    message: error.message || 'Unknown Error',
                    stack: error.stack || 'No stack trace',
                    code: error.code || 'UNKNOWN'
                },
                context: {
                    url: window.location.pathname,
                    userAgent: navigator.userAgent
                },
                status: 'new',
                createdAt: new Date().toISOString()
            });
            setReported(true);
            setTimeout(() => {
                clearError();
            }, 1000);
        } catch (e) {
            console.error("Failed to report error", e);
            setReporting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-neutral-900 border border-red-900/50 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl relative animate-in zoom-in-95 duration-200">
                <button
                    onClick={clearError}
                    className="absolute top-4 right-4 text-neutral-500 hover:text-white transition-colors"
                >
                    <X size={20} />
                </button>

                <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 bg-red-900/20 rounded-full flex items-center justify-center mb-4 text-red-500">
                        <AlertTriangle size={32} />
                    </div>

                    <h3 className="text-xl font-black text-white uppercase tracking-tight mb-2">
                        {error.title || t('common.error')}
                    </h3>

                    <p className="text-neutral-400 text-sm mb-6">
                        {error.message}
                    </p>

                    {error.code && (
                        <div className="bg-black/50 px-3 py-1 rounded text-[10px] font-mono text-neutral-600 mb-6">
                            Code: {error.code}
                        </div>
                    )}

                    <div className="flex gap-2 w-full">
                        <button
                            onClick={handleReport}
                            disabled={reporting || reported}
                            className={`flex-1 flex items-center justify-center gap-2 font-bold uppercase tracking-wider py-3 rounded-xl transition-colors ${reported ? 'bg-green-600 text-white' : 'bg-neutral-800 hover:bg-neutral-700 text-neutral-300'
                                }`}
                        >
                            {reported ? <Check size={18} /> : <Bug size={18} />}
                            {reported ? t('common.sent') : t('common.report')}
                        </button>
                        <button
                            onClick={clearError}
                            className="flex-1 bg-red-600 hover:bg-red-500 text-white font-bold uppercase tracking-wider py-3 rounded-xl transition-colors"
                        >
                            {t('common.accept')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ErrorModal;
